/**
 * walkHereSettings — the two things a right-click "Walk here" lets you change:
 * the prompt it generates from, and the speed it walks at.
 *
 * A module singleton with a subscribe, the same shape as walkHereState, because
 * the same rule applies: the values are read by an orchestrator that is not a
 * React component (runWalkHere), and putting them in React state would mean the
 * command could only be run from inside a render tree.
 *
 * DEFAULTS ARE THE OLD BEHAVIOUR, exactly. Until something is edited, a
 * walk-here click builds byte-identically the request it built before this
 * existed — which is what makes the editor safe to add to a gesture whose whole
 * point is that it is one click.
 *
 * Persisted per browser (localStorage). It is a personal preference about how a
 * dev-facing gesture behaves, not server state and not a draft of anything, so
 * the "drafts must be tagged with their API target" rule in CLAUDE.md does not
 * reach it — there is nothing here that could shadow a server response.
 */
import { WALK_SPEED } from './walkHerePlan.js'

/** The base prompt. Caner's guidance: a plain locomotion description is enough —
 *  the route2d constraint, not the prose, is what makes it FOLLOW the path. The
 *  prose still sets the gait, and "forwards" is doing work there: it asks for
 *  ordinary forward locomotion rather than leaving the model free to satisfy the
 *  same route by sidestepping or backing along it. */
export const DEFAULT_WALK_PROMPT = 'A person walks forwards'

const STORAGE_KEY = 'aos-walk-here-v1'

/** Speed bounds. Below the floor the duration clamp turns a walk into a creep
 *  (clampRouteDuration floors at 1.5 s); above the ceiling the route outruns
 *  what the model produces as walking and the clip goes unstable — the same
 *  reason MAX_WALK_M exists. */
export const MIN_WALK_SPEED = 0.2
export const MAX_WALK_SPEED = 4

const DEFAULTS = { prompt: DEFAULT_WALK_PROMPT, walkSpeed: WALK_SPEED }

/**
 * Gaits, as prompt + speed together.
 *
 * The two have to move as a pair: the prose sets what the model produces and the
 * speed sets how long the clip is for the route, so "runs" at 1.3 m/s asks for a
 * run played out over a walk's worth of time and comes back as neither. That
 * pairing is the whole reason these are presets rather than two independent
 * fields with a note.
 *
 * 3 m/s for a run is a jog rather than a sprint, and it stays inside the stable
 * zone: MAX_WALK_M caps a route at 13 m, which at 3 m/s is ~4.3 s, well under
 * the 15 s where clampRouteDuration tops out and generations go unstable.
 */
export const WALK_PRESETS = [
  { id: 'walk', label: 'Walk', prompt: DEFAULT_WALK_PROMPT, walkSpeed: WALK_SPEED },
  { id: 'run', label: 'Run', prompt: 'A person runs', walkSpeed: 3 },
]

function clean(raw) {
  const prompt = typeof raw?.prompt === 'string' && raw.prompt.trim() ? raw.prompt.trim() : DEFAULTS.prompt
  const n = Number(raw?.walkSpeed)
  const walkSpeed = Number.isFinite(n) ? Math.min(MAX_WALK_SPEED, Math.max(MIN_WALK_SPEED, n)) : DEFAULTS.walkSpeed
  return { prompt, walkSpeed }
}

function load() {
  if (typeof localStorage === 'undefined') return { ...DEFAULTS }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    // A stored value that has gone bad reverts to the defaults rather than
    // wedging the gesture — the worst case is a walk that behaves as it always
    // did, which is the safe direction for something with no UI of its own.
    return raw ? clean(JSON.parse(raw)) : { ...DEFAULTS }
  } catch {
    return { ...DEFAULTS }
  }
}

let state = load()
const listeners = new Set()

function emit() {
  for (const fn of listeners) {
    try { fn(state) } catch (err) { console.warn('[walkHereSettings] listener threw:', err) }
  }
}

export const walkHereSettings = {
  getState: () => state,

  subscribe(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  /** Merge a partial update. Values are cleaned, so a half-typed field can never
   *  reach the request as NaN or an empty prompt. */
  set(patch) {
    state = clean({ ...state, ...patch })
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* private mode */ }
    }
    emit()
    return state
  },

  /** Apply a gait by id. Unknown id is a no-op rather than a throw — a preset
   *  can be removed from the list without breaking a caller that still names it. */
  applyPreset(id) {
    const p = WALK_PRESETS.find(x => x.id === id)
    if (!p) return state
    return this.set({ prompt: p.prompt, walkSpeed: p.walkSpeed })
  },

  /** The preset the current values match, or null for a hand-edited pair. Both
   *  fields must match: half a preset is not that preset, and highlighting it as
   *  though it were would misreport what is about to be generated. */
  activePreset() {
    return WALK_PRESETS.find(p => p.prompt === state.prompt && p.walkSpeed === state.walkSpeed) ?? null
  },

  /** Back to the shipped behaviour. */
  reset() {
    state = { ...DEFAULTS }
    if (typeof localStorage !== 'undefined') {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* private mode */ }
    }
    emit()
    return state
  },

  /** True while both values are the shipped ones — the editor badges this so a
   *  walk behaving unexpectedly can be traced to a setting at a glance. */
  isDefault: () => state.prompt === DEFAULTS.prompt && state.walkSpeed === DEFAULTS.walkSpeed,

  defaults: () => ({ ...DEFAULTS }),
}

if (typeof window !== 'undefined') window.__WALK_HERE_SETTINGS__ = walkHereSettings
